/**
 * Shared quiz report helpers.
 * Downloaded reports include only incorrect answers and keep the original
 * question serial from the uploaded quiz file (not display/shuffle order).
 */

const csvCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

export const WRONG_ANSWERS_EMPTY_MESSAGE =
  'All answers correct — no wrong answers to review.';

/** Parse a 1-based question serial from stored quiz/file data. */
export const parsePositiveSerial = (value) => {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(String(value).trim());
  if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) return null;
  return n;
};

/**
 * Original serial from the uploaded quiz file.
 * Prefer stored questionNumber, then numeric questionId from the parser.
 * Fall back to position in the full answers array (file order), never the
 * wrong-answer-only index.
 */
export const getOriginalQuestionNumber = (answer, allAnswers = []) => {
  const fromField = parsePositiveSerial(answer?.questionNumber);
  if (fromField != null) return fromField;

  const fromId = parsePositiveSerial(answer?.questionId);
  if (fromId != null) return fromId;

  if (Array.isArray(allAnswers) && allAnswers.length) {
    const idx = allAnswers.indexOf(answer);
    if (idx >= 0) return idx + 1;
  }
  return null;
};

/** Serial on a parsed quiz question (upload file order / numbering). */
export const resolveQuestionNumberFromQuizItem = (question, index) => {
  const fromField = parsePositiveSerial(question?.questionNumber);
  if (fromField != null) return fromField;
  const fromId = parsePositiveSerial(question?.id);
  if (fromId != null) return fromId;
  return index + 1;
};

export const getWrongAnswersForReport = (answers = []) =>
  (answers || []).filter((a) => !a.isCorrect);

export const buildWrongAnswerTableRows = (answers = []) => {
  const all = answers || [];
  return getWrongAnswersForReport(all).map((a) => [
    String(getOriginalQuestionNumber(a, all) ?? ''),
    String(a.questionText ?? ''),
    a.selectedAnswerText || '(not answered)',
    String(a.correctAnswerText ?? ''),
  ]);
};

export const buildWrongAnswersCsv = ({
  userEmail,
  sessionTitle,
  score,
  totalQuestions,
  answers = [],
  completedAt,
} = {}) => {
  const all = answers || [];
  const wrong = getWrongAnswersForReport(all);
  const completed =
    completedAt instanceof Date
      ? completedAt
      : completedAt
        ? new Date(completedAt)
        : new Date();

  const rows = [
    ['User', userEmail || ''],
    ['Session', sessionTitle || 'Quiz'],
    ['Completed', Number.isNaN(completed.getTime()) ? '' : completed.toISOString()],
    ['Score', `${score ?? 0} / ${totalQuestions ?? 0}`],
    ['Wrong answers', String(wrong.length)],
    [],
  ];

  if (wrong.length === 0) {
    rows.push([WRONG_ANSWERS_EMPTY_MESSAGE]);
  } else {
    rows.push(['Question No.', 'Question', 'Your Answer', 'Correct Answer']);
    rows.push(...buildWrongAnswerTableRows(all));
  }

  return rows.map((row) => row.map(csvCell).join(',')).join('\n');
};
